import { useEffect, useRef, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';

import Button from '@/components/Button/Button';

/** Seconds the toast counts down before applying the update on its own. */
const AUTO_UPDATE_SECONDS = 20;
/** How often to ask the server for a new service worker while the app is open. */
const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;
/** Fallback delay before forcing a reload if the new worker never takes control. */
const RELOAD_FALLBACK_MS = 4000;
/** Scales the small buttons up on large screens so the toast is noticeable on big monitors. */
const BUTTON_SIZE_CLASSES = 'lg:px-4 lg:py-2 lg:text-sm 2xl:px-5 2xl:py-2.5 2xl:text-base';

/**
 * Service-worker update toast: when a new build is waiting, shows a countdown
 * and refreshes automatically when it hits zero. "Later" defers the update to
 * the next app launch. Also re-checks for updates hourly and whenever the app
 * returns to the foreground, so an installed PWA left open for days still
 * picks up new versions.
 */
export default function PwaUpdatePrompt() {
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      registrationRef.current = registration ?? null;
    },
  });

  const [secondsLeft, setSecondsLeft] = useState(AUTO_UPDATE_SECONDS);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    const check = () => {
      // Offline or transient network failure — try again next cycle.
      void registrationRef.current?.update().catch(() => {});
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') check();
    };

    const interval = window.setInterval(check, UPDATE_CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  const applyUpdate = () => {
    setUpdating(true);
    // The plugin only reloads when workbox-window flags the new SW as an
    // update, which is false on pages loaded without SW control (e.g. after a
    // hard reload) — reload ourselves when the new SW takes over, with a
    // timeout fallback in case control is never transferred.
    navigator.serviceWorker?.addEventListener('controllerchange', () => window.location.reload(), { once: true });
    window.setTimeout(() => window.location.reload(), RELOAD_FALLBACK_MS);
    void updateServiceWorker(true);
  };

  useEffect(() => {
    if (!needRefresh || updating) return;
    setSecondsLeft(AUTO_UPDATE_SECONDS);
    const timer = window.setInterval(() => setSecondsLeft((s) => s - 1), 1000);
    return () => window.clearInterval(timer);
  }, [needRefresh, updating]);

  useEffect(() => {
    if (needRefresh && !updating && secondsLeft <= 0) {
      applyUpdate();
    }
  }, [needRefresh, updating, secondsLeft]);

  if (!needRefresh) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="bg-slate/95 border-storm fixed inset-x-4 bottom-[calc(1rem+env(safe-area-inset-bottom))] z-50 mx-auto flex max-w-md flex-col gap-3 rounded-2xl border p-4 shadow-lg shadow-black/40 backdrop-blur sm:bottom-8 lg:max-w-lg lg:gap-4 lg:p-6 2xl:bottom-12 2xl:max-w-xl 2xl:gap-5 2xl:p-8"
    >
      <div className="lg:space-y-1">
        <p className="text-pearl font-medium lg:text-lg 2xl:text-xl">New version available</p>
        <p className="text-silver text-sm 2xl:text-base">
          {updating ? 'Updating…' : `Updating automatically in ${Math.max(secondsLeft, 0)}s…`}
        </p>
      </div>
      <div className="flex items-center justify-end gap-2 lg:gap-3">
        <Button
          text="Later"
          variant="outlined"
          color="secondary"
          size="small"
          className={BUTTON_SIZE_CLASSES}
          disabled={updating}
          onClick={() => setNeedRefresh(false)}
        />
        <Button text="Update now" size="small" className={BUTTON_SIZE_CLASSES} disabled={updating} onClick={applyUpdate} />
      </div>
    </div>
  );
}
